import React from "react";
import {
  Box,
  Dialog,
  Divider,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { AlertTriangle, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ButtonPrimary } from "src/UI/ButtonPrimary";
import { ButtonSecondary } from "src/UI/ButtonSecondary";

export type WizardAbandonMode = "create" | "edit";

export interface WizardAbandonCopy {
  title?: string;
  description?: string;
}

interface WizardAbandonDialogProps {
  open: boolean;
  mode: WizardAbandonMode;
  copy?: WizardAbandonCopy;
  onClose: () => void;
  onAbandon: () => void;
  onSaveDraft?: () => void;
}

const WizardAbandonDialog: React.FC<WizardAbandonDialogProps> = ({
  open,
  mode,
  copy,
  onClose,
  onAbandon,
  onSaveDraft,
}) => {
  const { t } = useTranslation("wizard-abandon");
  const theme = useTheme();

  const titleKey = mode === "edit" ? "edit.title" : "create.title";
  const descKey = mode === "edit" ? "edit.description" : "create.description";
  const showCloseIcon = mode === "create" && !!onSaveDraft;

  const abandonLabel =
    mode === "edit" ? t("edit.abandon") : t("create.abandon");
  let primaryLabel = t("create.continue");
  let onPrimary = onClose;
  if (mode === "edit") {
    primaryLabel = t("edit.continue");
  } else if (onSaveDraft) {
    primaryLabel = t("create.save-draft");
    onPrimary = onSaveDraft;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          maxWidth: "600px",
          backgroundColor: theme.palette.background.default,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      {showCloseIcon && (
        <IconButton
          onClick={onClose}
          aria-label="close"
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            color: theme.palette.text.primary,
          }}
        >
          <X size={20} />
        </IconButton>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          gap: "16px",
          padding: "32px",
          color: theme.palette.text.primary,
        }}
      >
        <AlertTriangle size={40} />
        <Typography
          sx={{
            fontSize: "24px",
            fontWeight: 700,
            lineHeight: 1.2,
            color: theme.palette.text.primary,
            overflowWrap: "anywhere",
          }}
        >
          {copy?.title ?? t(titleKey)}
        </Typography>
        <Typography
          sx={{
            fontSize: "16px",
            fontWeight: 400,
            lineHeight: 1.5,
            color: theme.palette.text.primary,
            whiteSpace: "pre-line",
          }}
        >
          {copy?.description ?? t(descKey)}
        </Typography>
        <Divider flexItem />
        <Box sx={{ display: "flex", gap: 1.5, width: "100%" }}>
          <ButtonSecondary size="md" onClick={onAbandon} sx={{ flex: 1 }}>
            {abandonLabel}
          </ButtonSecondary>
          <ButtonPrimary size="md" onClick={onPrimary} sx={{ flex: 1 }}>
            {primaryLabel}
          </ButtonPrimary>
        </Box>
      </Box>
    </Dialog>
  );
};

export default WizardAbandonDialog;
